import React, { useEffect, useState } from "react";
import {
  initInstallPrompt,
  isInstallable,
  isStandalone,
  onInstallabilityChange,
  promptInstall,
  isIOS,
  isAndroid,
  getBrowserKind,
} from "../lib/pwa";
import { Download, Smartphone, Monitor, Share } from "lucide-react";

export default function InstallButton({ compact = false }) {
  const [available, setAvailable] = useState(isInstallable());
  const [installed, setInstalled] = useState(isStandalone());
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    initInstallPrompt();
    const off = onInstallabilityChange((v) => setAvailable(v));
    const onInstalled = () => setInstalled(true);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      off();
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const handleClick = async () => {
    if (available) {
      const res = await promptInstall();
      if (res?.outcome === "accepted") {
        setInstalled(true);
      } else if (res?.outcome === "unavailable") {
        setShowModal(true);
      }
      return;
    }
    setShowModal(true);
  };

  const browser = getBrowserKind();

  return (
    <>
      <button
        onClick={handleClick}
        data-testid="install-app-button"
        className={
          compact
            ? "inline-flex items-center gap-2 rounded bg-orange-600 px-3 py-2 text-sm font-semibold text-white hover:bg-orange-700 transition-colors"
            : "inline-flex items-center gap-2 rounded bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-700 transition-colors shadow-sm"
        }
        title="Installa l'app sul tuo dispositivo"
      >
        <Download className="h-4 w-4" />
        {compact ? "Installa" : "Scarica App"}
      </button>

      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-4"
          onClick={() => setShowModal(false)}
          data-testid="install-instructions-backdrop"
        >
          <div
            className="panel max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="install-instructions-modal"
          >
            <div className="flex items-center gap-2 mb-3">
              {isIOS() ? (
                <Smartphone className="h-5 w-5 text-blue-900" />
              ) : isAndroid() ? (
                <Smartphone className="h-5 w-5 text-blue-900" />
              ) : (
                <Monitor className="h-5 w-5 text-blue-900" />
              )}
              <h3 className="text-lg font-semibold">Come installare l'app</h3>
            </div>

            {isIOS() && (
              <>
                <p className="text-sm text-slate-600 mb-3">
                  Su iPhone / iPad bisogna usare <b>Safari</b> (non Chrome):
                </p>
                <ol className="space-y-2 text-sm text-slate-700 list-decimal pl-5">
                  <li>
                    Tocca il pulsante <Share className="inline w-4 h-4 -mt-0.5" />{" "}
                    <b>Condividi</b> in basso
                  </li>
                  <li>
                    Scorri e seleziona{" "}
                    <b>"Aggiungi alla schermata Home"</b>
                  </li>
                  <li>
                    Conferma con <b>"Aggiungi"</b>
                  </li>
                  <li>
                    L'icona apparirà sulla Home. Apri l'app installata per
                    attivare le notifiche.
                  </li>
                </ol>
              </>
            )}

            {isAndroid() && (
              <>
                <p className="text-sm text-slate-600 mb-3">
                  Su Android usa <b>Chrome</b>:
                </p>
                <ol className="space-y-2 text-sm text-slate-700 list-decimal pl-5">
                  <li>
                    Tocca il menu <b>⋮</b> in alto a destra
                  </li>
                  <li>
                    Seleziona <b>"Installa app"</b> o{" "}
                    <b>"Aggiungi a schermata Home"</b>
                  </li>
                  <li>Conferma</li>
                </ol>
              </>
            )}

            {!isIOS() && !isAndroid() && (
              <>
                {browser === "chrome" || browser === "edge" ? (
                  <>
                    <p className="text-sm text-slate-600 mb-3">
                      Su {browser === "edge" ? "Edge" : "Chrome"} desktop:
                    </p>
                    <ol className="space-y-2 text-sm text-slate-700 list-decimal pl-5">
                      <li>
                        Guarda la barra degli indirizzi: cerca l'icona{" "}
                        <Download className="inline w-4 h-4 -mt-0.5" /> "Installa"
                        a destra dell'URL
                      </li>
                      <li>Cliccaci sopra e conferma</li>
                      <li>
                        In alternativa: menu <b>⋮</b> → <b>"Installa Reti Molluschi…"</b>
                      </li>
                      <li>
                        L'app apparirà come finestra dedicata sul desktop e nel
                        menu Start/Applicazioni.
                      </li>
                    </ol>
                  </>
                ) : browser === "firefox" ? (
                  <>
                    <p className="text-sm text-slate-600 mb-3">
                      Firefox desktop non supporta l'installazione PWA nativa.
                      Usa Chrome o Edge per installare l'app come finestra
                      dedicata. Puoi comunque usare l'app via browser.
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-slate-600">
                    Apri il menu del tuo browser e cerca{" "}
                    <b>"Installa app"</b> o <b>"Aggiungi a schermata Home"</b>.
                    Per la migliore esperienza ti consigliamo Chrome o Edge.
                  </p>
                )}
              </>
            )}

            <button
              onClick={() => setShowModal(false)}
              className="mt-5 w-full bg-slate-900 text-white rounded py-2 text-sm font-semibold hover:bg-slate-800 transition-colors"
              data-testid="install-instructions-close"
            >
              Ho capito
            </button>
          </div>
        </div>
      )}
    </>
  );
}
